import { useState } from 'react';
import { chunk } from 'lodash-es';

import { EventBusEvent } from 'common/enums/EventBus';
import eventBus from 'common/event-bus/EventBus';
import { executeQuery } from 'common/gql';
import type { AssocMap } from 'common/gql/fetch/types';
import type {
  AdditionalInfoResourcesQuery,
  AdditionalInfoResourcesQueryVariables,
  DeleteAttachmentMutationVariables,
  UploadAttachmentsMutation,
  UploadAttachmentsMutationVariables,
} from 'common/gql/generated/schema';
import type { DispatchState } from 'common/types/React';
import type { ReclamationUploadedPicturesChangeState } from 'common/types/reclamation';
import deleteAttachmentMutation from '../../gql/mutation/deleteAttachment.gql';
import uploadAttachmentsMutation from '../../gql/mutation/uploadAttachments.gql';
import additionalInfoResourcesQuery from '../../gql/query/additionalInfoResources.gql';

interface CommonActionOptions {
  reclamationId: string;
  itemId: string;
  setPictures: DispatchState<string[]>;
}

interface UploadAttachmentArgs {
  reclamationId: string;
  itemId: string;
  files: File[];
}

const emitPicturesChangeEvent = (itemId: string, pictures: string[]) =>
  eventBus.emit<ReclamationUploadedPicturesChangeState>(
    EventBusEvent.ReclamationUploadedPicturesStateChange,
    {
      itemId,
      pictures,
    }
  );

const fetchResources = async (reclamationId: string, itemId: string) => {
  const { data } = await executeQuery<
    AdditionalInfoResourcesQuery,
    AdditionalInfoResourcesQueryVariables
  >({
    query: additionalInfoResourcesQuery,
    variables: {
      reclamationId,
      itemId,
    },
    useApq: true,
  });

  const resources = data?.resources ?? [];

  emitPicturesChangeEvent(itemId, resources);

  return resources;
};

const deleteAttachment = async (reclamationId: string, itemId: string, filepath: string) => {
  await executeQuery<void, DeleteAttachmentMutationVariables>({
    query: deleteAttachmentMutation,
    variables: { reclamationId, itemId, filepath },
    useApq: true,
  });

  return fetchResources(reclamationId, itemId);
};

const getFilesMap = (files: File[]) => {
  const map: AssocMap = new Map();

  files.forEach((file, i) => {
    map.set(`files.${i}.file`, {
      name: i.toString(),
      value: file,
    });
  });

  return map;
};

const uploadAttachment = async ({ reclamationId, itemId, files }: UploadAttachmentArgs) => {
  // upload images in the chunks of 4 per query to avoid server timeouts
  // https://home24.atlassian.net/browse/FP-1479
  const chunks = chunk(files, 4);
  let lastResponse: UploadAttachmentsMutation = { attachments: [] };

  for (const filesChunk of chunks) {
    const variables = {
      files: filesChunk.map(() => ({
        reclamationId,
        itemId,
        file: null,
      })),
    };

    lastResponse = await executeQuery<
      UploadAttachmentsMutation,
      UploadAttachmentsMutationVariables
    >({
      query: uploadAttachmentsMutation,
      variables,
      assocMap: getFilesMap(filesChunk),
    });
  }

  return lastResponse;
};

const useFetchPictures =
  ({ reclamationId, itemId, setPictures }: CommonActionOptions) =>
  async () => {
    const resources = await fetchResources(reclamationId, itemId);
    setPictures(resources);
  };

const useUploadPictures =
  ({ reclamationId, itemId, setPictures }: CommonActionOptions) =>
  async (files: File[]) => {
    const { attachments } = await uploadAttachment({
      reclamationId,
      itemId,
      files,
    });
    setPictures(attachments);
    emitPicturesChangeEvent(itemId, attachments);
  };

const useDeletePicture =
  ({ reclamationId, itemId, setPictures }: CommonActionOptions) =>
  async (filepath: string) => {
    const resources = await deleteAttachment(reclamationId, itemId, filepath);
    setPictures(resources);
  };

export const usePictures = (reclamationId: string, itemId: string) => {
  const [pictures, setPictures] = useState<string[]>([]);
  const options = {
    reclamationId,
    itemId,
    setPictures,
  };
  const fetchPictures = useFetchPictures(options);
  const uploadPictures = useUploadPictures(options);
  const deletePicture = useDeletePicture(options);

  return {
    pictures,
    uploadPictures,
    deletePicture,
    fetchPictures,
  };
};

export const usePictureGuideState = (setIsNextHidden: (state: boolean) => void) => {
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const setGuideOpen = () => {
    setIsGuideOpen(true);
    setIsNextHidden(true);
  };
  const setGuideClosed = () => {
    setIsGuideOpen(false);
    setIsNextHidden(false);
  };

  return [isGuideOpen, setGuideOpen, setGuideClosed] as const;
};
